import { z } from 'zod'
import * as Sentry from '@sentry/react'
import { errorMessages } from '../shared/errorMessages'

/**
 * Remove Element Use Case
 * Handles removing elements from the canvas
 */

// Command schema for removing an element
export const RemoveElementCommandSchema = z.object({
  elementId: z.string().min(1),
})

export type RemoveElementCommand = z.infer<typeof RemoveElementCommandSchema>

// Result type
export type RemoveElementResult = {
  success: boolean
  removedElementId?: string
  error?: string
}

// Dependencies interface
type RemoveElementDependencies = {
  elementExists: (elementId: string) => boolean
  getSelectedElementId: () => string | null
}

// Factory function to create the use case
export const createRemoveElementUseCase = (deps: RemoveElementDependencies) => ({
  execute: async (command: RemoveElementCommand): Promise<RemoveElementResult> => {
    try {
      // Validate command
      const validatedCommand = RemoveElementCommandSchema.parse(command)

      // Check if element exists
      if (!deps.elementExists(validatedCommand.elementId)) {
        return {
          success: false,
          error: errorMessages.elementNotFound(validatedCommand.elementId),
        }
      }

      // TODO: Emit domain event
      // const selectedElementId = deps.getSelectedElementId()
      // const wasSelected = selectedElementId === validatedCommand.elementId
      // eventBus.emit(canvasEvents.elementRemoved(validatedCommand.elementId, wasSelected))

      return {
        success: true,
        removedElementId: validatedCommand.elementId,
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'removeElement' },
        extra: { elementId: command.elementId },
      })
      return {
        success: false,
        error: error instanceof Error ? error.message : errorMessages.failedToRemoveElement(),
      }
    }
  },
})
